import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, ComposedChart } from 'recharts';
const EnvironmentalComplianceDashboard: React.FC = () => {
  const kpis = [{
    title: 'ETP/STP Compliance %',
    value: '96%',
    change: '+3%',
    isPositive: true,
    subtitle: 'Effluent treatment'
  }, {
    title: 'Waste Disposal Compliance %',
    value: '94%',
    change: '+2%',
    isPositive: true,
    subtitle: 'Hazardous & non-hazardous'
  }, {
    title: 'Emission Level Index',
    value: '82',
    change: '-5',
    isPositive: true,
    subtitle: 'Below regulatory limits'
  }];
  // Compliance Trend Data
  const complianceTrendData = [{
    month: 'Jan',
    etp: 92,
    waste: 90,
    emission: 88
  }, {
    month: 'Feb',
    etp: 93,
    waste: 91,
    emission: 89
  }, {
    month: 'Mar',
    etp: 93,
    waste: 92,
    emission: 87
  }, {
    month: 'Apr',
    etp: 94,
    waste: 92,
    emission: 86
  }, {
    month: 'May',
    etp: 95,
    waste: 93,
    emission: 84
  }, {
    month: 'Jun',
    etp: 96,
    waste: 94,
    emission: 82
  }];
  // Waste Category Chart Data
  const wasteCategoryData = [{
    category: 'Hazardous Waste',
    value: 45,
    percentage: 32.1,
    color: '#ef4444'
  }, {
    category: 'E-Waste',
    value: 18,
    percentage: 12.9,
    color: '#f59e0b'
  }, {
    category: 'Plastic Waste',
    value: 28,
    percentage: 20.0,
    color: '#fbbf24'
  }, {
    category: 'Metal Scrap',
    value: 35,
    percentage: 25.0,
    color: '#3b82f6'
  }, {
    category: 'Paper/Cardboard',
    value: 14,
    percentage: 10.0,
    color: '#10b981'
  }];
  // Emissions vs Norms Data
  const emissionsVsNormsData = [{
    parameter: 'PM10',
    actual: 68,
    norm: 100,
    unit: 'μg/m³'
  }, {
    parameter: 'PM2.5',
    actual: 42,
    norm: 60,
    unit: 'μg/m³'
  }, {
    parameter: 'SO₂',
    actual: 35,
    norm: 80,
    unit: 'μg/m³'
  }, {
    parameter: 'NOₓ',
    actual: 58,
    norm: 80,
    unit: 'μg/m³'
  }, {
    parameter: 'CO',
    actual: 1.2,
    norm: 2.0,
    unit: 'mg/m³'
  }, {
    parameter: 'VOC',
    actual: 28,
    norm: 50,
    unit: 'mg/m³'
  }];
  // Environmental Compliance Log Data
  const environmentalComplianceLogData = [{
    logId: 'ENV-2024-001',
    date: '2024-01-15',
    plant: 'Manesar',
    category: 'ETP/STP',
    parameter: 'BOD Level',
    measuredValue: '28 mg/L',
    regulatoryLimit: '30 mg/L',
    status: 'Compliant',
    testingAgency: 'Internal Lab',
    certificateNo: 'LAB-2024-001',
    nextTestDate: '2024-02-15',
    responsiblePerson: 'EHS Manager',
    correctiveAction: 'N/A',
    remarks: 'Well within limits'
  }, {
    logId: 'ENV-2024-002',
    date: '2024-01-18',
    plant: 'Chennai',
    category: 'Waste Disposal',
    parameter: 'Hazardous Waste Manifest',
    measuredValue: 'Complete',
    regulatoryLimit: 'Required',
    status: 'Compliant',
    testingAgency: 'Authorized Vendor',
    certificateNo: 'HW-2024-001',
    nextTestDate: '2024-02-18',
    responsiblePerson: 'Facility Manager',
    correctiveAction: 'N/A',
    remarks: 'All documentation complete'
  }, {
    logId: 'ENV-2024-003',
    date: '2024-01-20',
    plant: 'Pune',
    category: 'Air Emissions',
    parameter: 'PM10',
    measuredValue: '72 μg/m³',
    regulatoryLimit: '100 μg/m³',
    status: 'Compliant',
    testingAgency: 'NABL Certified Lab',
    certificateNo: 'AIR-2024-001',
    nextTestDate: '2024-04-20',
    responsiblePerson: 'Environmental Officer',
    correctiveAction: 'N/A',
    remarks: 'Quarterly monitoring completed'
  }, {
    logId: 'ENV-2024-004',
    date: '2024-01-22',
    plant: 'Bangalore',
    category: 'ETP/STP',
    parameter: 'pH Level',
    measuredValue: '8.2',
    regulatoryLimit: '6.5-8.5',
    status: 'Compliant',
    testingAgency: 'Internal Lab',
    certificateNo: 'LAB-2024-002',
    nextTestDate: '2024-02-22',
    responsiblePerson: 'EHS Manager',
    correctiveAction: 'N/A',
    remarks: 'Daily monitoring ongoing'
  }, {
    logId: 'ENV-2024-005',
    date: '2024-01-25',
    plant: 'Manesar',
    category: 'Waste Disposal',
    parameter: 'E-Waste Disposal',
    measuredValue: '450 kg',
    regulatoryLimit: 'Authorized Disposal',
    status: 'Compliant',
    testingAgency: 'Authorized Recycler',
    certificateNo: 'EW-2024-001',
    nextTestDate: '2024-07-25',
    responsiblePerson: 'IT Manager',
    correctiveAction: 'N/A',
    remarks: 'Certificate of destruction received'
  }, {
    logId: 'ENV-2024-006',
    date: '2024-01-28',
    plant: 'Chennai',
    category: 'Air Emissions',
    parameter: 'NOₓ',
    measuredValue: '62 μg/m³',
    regulatoryLimit: '80 μg/m³',
    status: 'Compliant',
    testingAgency: 'NABL Certified Lab',
    certificateNo: 'AIR-2024-002',
    nextTestDate: '2024-04-28',
    responsiblePerson: 'Environmental Officer',
    correctiveAction: 'N/A',
    remarks: 'Stack emission monitoring completed'
  }, {
    logId: 'ENV-2024-007',
    date: '2024-02-01',
    plant: 'Pune',
    category: 'ETP/STP',
    parameter: 'COD Level',
    measuredValue: '95 mg/L',
    regulatoryLimit: '100 mg/L',
    status: 'Compliant',
    testingAgency: 'Internal Lab',
    certificateNo: 'LAB-2024-003',
    nextTestDate: '2024-03-01',
    responsiblePerson: 'EHS Manager',
    correctiveAction: 'N/A',
    remarks: 'Weekly monitoring on schedule'
  }, {
    logId: 'ENV-2024-008',
    date: '2024-02-03',
    plant: 'Bangalore',
    category: 'Waste Disposal',
    parameter: 'Plastic Waste Segregation',
    measuredValue: 'Compliant',
    regulatoryLimit: 'Required',
    status: 'Compliant',
    testingAgency: 'Internal Audit',
    certificateNo: 'IA-2024-001',
    nextTestDate: '2024-03-03',
    responsiblePerson: 'Facility Manager',
    correctiveAction: 'N/A',
    remarks: 'Color-coded bins in place'
  }, {
    logId: 'ENV-2024-009',
    date: '2024-02-05',
    plant: 'Manesar',
    category: 'Air Emissions',
    parameter: 'SO₂',
    measuredValue: '38 μg/m³',
    regulatoryLimit: '80 μg/m³',
    status: 'Compliant',
    testingAgency: 'NABL Certified Lab',
    certificateNo: 'AIR-2024-003',
    nextTestDate: '2024-05-05',
    responsiblePerson: 'Environmental Officer',
    correctiveAction: 'N/A',
    remarks: 'Ambient air quality monitoring'
  }, {
    logId: 'ENV-2024-010',
    date: '2024-02-08',
    plant: 'Chennai',
    category: 'ETP/STP',
    parameter: 'Total Suspended Solids',
    measuredValue: '85 mg/L',
    regulatoryLimit: '100 mg/L',
    status: 'Compliant',
    testingAgency: 'Internal Lab',
    certificateNo: 'LAB-2024-004',
    nextTestDate: '2024-03-08',
    responsiblePerson: 'EHS Manager',
    correctiveAction: 'N/A',
    remarks: 'STP performance satisfactory'
  }, {
    logId: 'ENV-2024-011',
    date: '2024-02-10',
    plant: 'Pune',
    category: 'Waste Disposal',
    parameter: 'Hazardous Waste Storage',
    measuredValue: 'Compliant',
    regulatoryLimit: '90 days max',
    status: 'Non-Compliant',
    testingAgency: 'Internal Audit',
    certificateNo: 'IA-2024-002',
    nextTestDate: '2024-02-20',
    responsiblePerson: 'EHS Manager',
    correctiveAction: 'Immediate disposal arranged with authorized vendor',
    remarks: 'Some waste stored beyond 90 days - corrective action taken'
  }, {
    logId: 'ENV-2024-012',
    date: '2024-02-12',
    plant: 'Bangalore',
    category: 'Air Emissions',
    parameter: 'VOC',
    measuredValue: '32 mg/m³',
    regulatoryLimit: '50 mg/m³',
    status: 'Compliant',
    testingAgency: 'NABL Certified Lab',
    certificateNo: 'AIR-2024-004',
    nextTestDate: '2024-05-12',
    responsiblePerson: 'Environmental Officer',
    correctiveAction: 'N/A',
    remarks: 'Painting booth emissions within limits'
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Compliant':
        return 'bg-green-100 text-green-700';
      case 'Non-Compliant':
        return 'bg-red-100 text-red-700';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'ETP/STP':
        return 'bg-blue-100 text-blue-700';
      case 'Waste Disposal':
        return 'bg-orange-100 text-orange-700';
      case 'Air Emissions':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  return <div data-id="element-1805">
      <DescriptionPanel title="Environmental Compliance" description="Comprehensive environmental compliance monitoring system tracking ETP/STP performance, waste disposal adherence, and air emission levels across all manufacturing facilities. Ensure regulatory compliance and environmental sustainability." insights={['ETP/STP compliance at 96% - all plants meeting effluent discharge standards', 'Waste disposal compliance improved to 94% - enhanced segregation and tracking', 'Emission Level Index at 82 - 18% below regulatory limits across all parameters', '1 non-compliance identified in Pune - hazardous waste storage duration exceeded, corrective action completed']} data-id="element-1806" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-1807">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1808">
          Environmental Compliance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1809">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-1810">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1811">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-1812">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-1813">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-1814">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-1815" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-1816" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Compliance Trend and Waste Category Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-1817">
        {/* Compliance Trend */}
        <Card className="p-6" data-id="element-1818">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1819">
            Compliance Trend
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1820">
            Monthly compliance percentage across environmental categories
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1821">
            <LineChart data={complianceTrendData} data-id="element-1822">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1823" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-1824" />
              <YAxis domain={[75, 100]} tick={{
              fontSize: 12
            }} data-id="element-1825" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1826" />
              <Legend data-id="element-1827" />
              <Line type="monotone" dataKey="etp" stroke="#3b82f6" strokeWidth={3} name="ETP/STP %" dot={{
              r: 5
            }} data-id="element-1828" />
              <Line type="monotone" dataKey="waste" stroke="#f59e0b" strokeWidth={3} name="Waste Disposal %" dot={{
              r: 5
            }} data-id="element-1829" />
              <Line type="monotone" dataKey="emission" stroke="#8b5cf6" strokeWidth={3} name="Emission Index" dot={{
              r: 5
            }} data-id="element-1830" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1831">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-1832">
              <div className="text-center" data-id="element-1833">
                <div className="text-gray-600" data-id="element-1834">ETP/STP</div>
                <div className="text-xl font-bold text-blue-600" data-id="element-1835">96%</div>
                <div className="text-xs text-gray-500" data-id="element-1836">current</div>
              </div>
              <div className="text-center" data-id="element-1837">
                <div className="text-gray-600" data-id="element-1838">Waste Disposal</div>
                <div className="text-xl font-bold text-orange-600" data-id="element-1839">94%</div>
                <div className="text-xs text-gray-500" data-id="element-1840">current</div>
              </div>
              <div className="text-center" data-id="element-1841">
                <div className="text-gray-600" data-id="element-1842">Emission Index</div>
                <div className="text-xl font-bold text-purple-600" data-id="element-1843">82</div>
                <div className="text-xs text-gray-500" data-id="element-1844">below limit</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Waste Category Chart */}
        <Card className="p-6" data-id="element-1845">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1846">
            Waste Category Chart
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1847">
            Distribution of waste generation by category (tons/month)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1848">
            <PieChart data-id="element-1849">
              <Pie data={wasteCategoryData} cx="50%" cy="50%" labelLine={false} label={({
              name,
              percentage
            }) => `${name}: ${percentage}%`} outerRadius={120} fill="#8884d8" dataKey="value" data-id="element-1850">
                {wasteCategoryData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-1851" />)}
              </Pie>
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1852" />
              <Legend data-id="element-1853" />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1854">
            <div className="grid grid-cols-3 gap-4 text-xs" data-id="element-1855">
              <div className="text-center" data-id="element-1856">
                <div className="text-gray-600" data-id="element-1857">Total Waste</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-1858">140 tons</div>
                <div className="text-xs text-gray-500" data-id="element-1859">per month</div>
              </div>
              <div className="text-center" data-id="element-1860">
                <div className="text-gray-600" data-id="element-1861">Hazardous</div>
                <div className="text-lg font-bold text-red-600" data-id="element-1862">45 tons</div>
                <div className="text-xs text-gray-500" data-id="element-1863">32.1%</div>
              </div>
              <div className="text-center" data-id="element-1864">
                <div className="text-gray-600" data-id="element-1865">Recycled</div>
                <div className="text-lg font-bold text-green-600" data-id="element-1866">77 tons</div>
                <div className="text-xs text-gray-500" data-id="element-1867">55.0%</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Emissions vs Norms */}
      <Card className="p-6 mb-8" data-id="element-1868">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1869">
          Emissions vs Norms
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-1870">
          Actual emission levels compared to regulatory limits
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-1871">
          <ComposedChart data={emissionsVsNormsData} data-id="element-1872">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1873" />
            <XAxis dataKey="parameter" tick={{
            fontSize: 12
          }} data-id="element-1874" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-1875" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-1876" />
            <Legend data-id="element-1877" />
            <Bar dataKey="actual" fill="#3b82f6" name="Actual Level" data-id="element-1878" />
            <Line type="monotone" dataKey="norm" stroke="#ef4444" strokeWidth={3} name="Regulatory Limit" dot={{
            r: 5
          }} data-id="element-1879" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1880">
          <div className="grid grid-cols-6 gap-4 text-xs" data-id="element-1881">
            <div className="text-center" data-id="element-1882">
              <div className="text-gray-600" data-id="element-1883">PM10</div>
              <div className="text-sm font-bold text-blue-600" data-id="element-1884">68</div>
              <div className="text-xs text-gray-500" data-id="element-1885">vs 100 limit</div>
            </div>
            <div className="text-center" data-id="element-1886">
              <div className="text-gray-600" data-id="element-1887">PM2.5</div>
              <div className="text-sm font-bold text-blue-600" data-id="element-1888">42</div>
              <div className="text-xs text-gray-500" data-id="element-1889">vs 60 limit</div>
            </div>
            <div className="text-center" data-id="element-1890">
              <div className="text-gray-600" data-id="element-1891">SO₂</div>
              <div className="text-sm font-bold text-blue-600" data-id="element-1892">35</div>
              <div className="text-xs text-gray-500" data-id="element-1893">vs 80 limit</div>
            </div>
            <div className="text-center" data-id="element-1894">
              <div className="text-gray-600" data-id="element-1895">NOₓ</div>
              <div className="text-sm font-bold text-blue-600" data-id="element-1896">58</div>
              <div className="text-xs text-gray-500" data-id="element-1897">vs 80 limit</div>
            </div>
            <div className="text-center" data-id="element-1898">
              <div className="text-gray-600" data-id="element-1899">CO</div>
              <div className="text-sm font-bold text-blue-600" data-id="element-1900">1.2</div>
              <div className="text-xs text-gray-500" data-id="element-1901">vs 2.0 limit</div>
            </div>
            <div className="text-center" data-id="element-1902">
              <div className="text-gray-600" data-id="element-1903">VOC</div>
              <div className="text-sm font-bold text-blue-600" data-id="element-1904">28</div>
              <div className="text-xs text-gray-500" data-id="element-1905">vs 50 limit</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Environmental Compliance Log */}
      <Card className="p-6" data-id="element-1906">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-1907">
          Environmental Compliance Log
        </h3>
        <div className="overflow-x-auto" data-id="element-1908">
          <table className="w-full" data-id="element-1909">
            <thead data-id="element-1910">
              <tr className="border-b-2 border-gray-300" data-id="element-1911">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1912">
                  Log ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1913">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1914">
                  Plant
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1915">
                  Category
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1916">
                  Parameter
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1917">
                  Measured Value
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1918">
                  Regulatory Limit
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1919">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1920">
                  Testing Agency
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1921">
                  Certificate No.
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1922">
                  Next Test Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1923">
                  Responsible Person
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1924">
                  Corrective Action
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1925">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1926">
              {environmentalComplianceLogData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1927">
                  <td className="py-3 px-4 text-sm font-medium text-blue-600" data-id="element-1928">
                    {row.logId}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1929">
                    {row.date}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-1930">
                    {row.plant}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1931">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getCategoryColor(row.category)}`} data-id="element-1932">
                      {row.category}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1933">
                    {row.parameter}
                  </td>
                  <td className="py-3 px-4 text-sm font-semibold text-gray-900" data-id="element-1934">
                    {row.measuredValue}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1935">
                    {row.regulatoryLimit}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1936">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-1937">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1938">
                    {row.testingAgency}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1939">
                    {row.certificateNo}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1940">
                    {row.nextTestDate}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1941">
                    {row.responsiblePerson}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-1942">
                    {row.correctiveAction}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-1943">
                    {row.remarks}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-1944">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-1945">
            <div className="text-center" data-id="element-1946">
              <div className="text-gray-600" data-id="element-1947">Total Tests</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-1948">12</div>
              <div className="text-xs text-gray-500" data-id="element-1949">this period</div>
            </div>
            <div className="text-center" data-id="element-1950">
              <div className="text-gray-600" data-id="element-1951">Compliant</div>
              <div className="text-xl font-bold text-green-600" data-id="element-1952">11</div>
              <div className="text-xs text-gray-500" data-id="element-1953">91.7%</div>
            </div>
            <div className="text-center" data-id="element-1954">
              <div className="text-gray-600" data-id="element-1955">Non-Compliant</div>
              <div className="text-xl font-bold text-red-600" data-id="element-1956">1</div>
              <div className="text-xs text-gray-500" data-id="element-1957">8.3%</div>
            </div>
            <div className="text-center" data-id="element-1958">
              <div className="text-gray-600" data-id="element-1959">ETP/STP Tests</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-1960">5</div>
              <div className="text-xs text-gray-500" data-id="element-1961">all compliant</div>
            </div>
            <div className="text-center" data-id="element-1962">
              <div className="text-gray-600" data-id="element-1963">Waste Audits</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-1964">4</div>
              <div className="text-xs text-gray-500" data-id="element-1965">1 issue</div>
            </div>
            <div className="text-center" data-id="element-1966">
              <div className="text-gray-600" data-id="element-1967">Emission Tests</div>
              <div className="text-xl font-bold text-purple-600" data-id="element-1968">3</div>
              <div className="text-xs text-gray-500" data-id="element-1969">all compliant</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default EnvironmentalComplianceDashboard;